/**
 * assets/vpv-video-block.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles inline and modal playback for vpv-video-block.liquid.
 *
 * Native <video>  — full poster re-show on pause / end.
 * Vimeo / YouTube — poster hides on play. Re-show requires Vimeo Player SDK
 *                   or YouTube IFrame API; add those if needed (see bottom of
 *                   this file for hooks).
 *
 * Load this file once in your theme:
 *   {{ 'vpv-video-block.js' | asset_url | script_tag }}
 * Or via <script src="{{ 'vpv-video-block.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  // ── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Parse a Vimeo or YouTube URL into an embed src with autoplay.
   * Returns null if the URL is unrecognised.
   * @param {string} url
   * @returns {{ type: string, src: string } | null}
   */
  function parseEmbedUrl(url) {
    if (!url) return null;

    // YouTube — watch?v=, youtu.be/, shorts/, embed/
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    if (ytMatch) {
      return {
        type: 'youtube',
        src: `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0&playsinline=1`
      };
    }

    // Vimeo — vimeo.com/12345 or player.vimeo.com/video/12345
    const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeoMatch) {
      return {
        type: 'vimeo',
        src: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&dnt=1`
      };
    }

    console.warn('[vpv-video-block] Unrecognised embed URL:', url);
    return null;
  }

  /**
   * Build an autoplay iframe element.
   * @param {string} src
   * @param {string} title
   * @returns {HTMLIFrameElement}
   */
  function buildIframe(src, title) {
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = title || 'Video player';
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    return iframe;
  }

  // ── Core block controller ─────────────────────────────────────────────────

  /**
   * Initialise a single [data-vpv-video-block] element.
   * @param {HTMLElement} block
   */
  function initBlock(block) {
    const videoType  = block.dataset.videoType;   // 'native' | 'youtube' | 'vimeo'
    const embedUrl   = block.dataset.embedUrl;
    const useModal   = block.dataset.modal === 'true';
    const blockId    = block.dataset.blockId;

    const posterEl   = block.querySelector('[data-vpv-poster]');
    const playerEl   = block.querySelector('[data-vpv-player]');
    const playBtn    = block.querySelector('[data-vpv-play-btn]');

    // Modal elements (may be null if not enabled)
    const modalEl    = useModal
      ? document.getElementById(`vpv-modal-${blockId}`)
      : null;
    const modalContent = modalEl
      ? modalEl.querySelector('[data-vpv-modal-content]')
      : null;

    if (!playBtn) return;

    // ── Show poster / hide player ─────────────────────────────────────────
    function showPoster() {
      posterEl.hidden = false;
      posterEl.removeAttribute('aria-hidden');
      posterEl.classList.remove('is-hidden');
      playerEl.hidden = true;
    }

    // ── Hide poster / show player ─────────────────────────────────────────
    function hidePoster() {
      posterEl.hidden = true;
      posterEl.setAttribute('aria-hidden', 'true');
      posterEl.classList.add('is-hidden');
      playerEl.hidden = false;
    }

    // ── Attach native <video> events so poster re-shows on pause/end ──────
    function attachNativeEvents(videoEl) {
      videoEl.addEventListener('pause', () => {
        // pause fires briefly during seeking; ignore if user is scrubbing
        if (!videoEl.seeking) showPoster();
      });
      videoEl.addEventListener('ended', showPoster);
    }

    // ── Play inline ───────────────────────────────────────────────────────
    function playInline() {
      if (videoType === 'native') {
        const videoEl = playerEl.querySelector('video');
        if (!videoEl) return;

        hidePoster();
        videoEl.play().catch(() => {
          // Autoplay blocked (shouldn't happen after a click, but be safe)
          videoEl.muted = false;
          videoEl.play();
        });
        attachNativeEvents(videoEl);

      } else {
        // Embed — inject iframe (prevents preloading before click)
        if (!playerEl.querySelector('iframe')) {
          const parsed = parseEmbedUrl(embedUrl);
          if (!parsed) return;
          playerEl.classList.add('vpv-vb__player--embed');
          playerEl.appendChild(buildIframe(parsed.src, 'Video player'));
        }
        hidePoster();
        // See bottom of file for Vimeo/YT API hooks if you need poster re-show
      }
    }

    // ── Play in modal ─────────────────────────────────────────────────────
    function playModal() {
      if (!modalEl || !modalContent) return;

      // Clear any previous content
      modalContent.innerHTML = '';

      if (videoType === 'native') {
        const videoEl = playerEl.querySelector('video');
        if (!videoEl) return;
        // Clone so we don't move the original out of the DOM
        const clone = videoEl.cloneNode(true);
        clone.removeAttribute('hidden');
        clone.autoplay = true;
        clone.muted = false;
        clone.controls = true;
        modalContent.appendChild(clone);
        clone.play().catch(() => {});

      } else {
        const parsed = parseEmbedUrl(embedUrl);
        if (!parsed) return;
        modalContent.appendChild(buildIframe(parsed.src, 'Video player'));
      }

      modalEl.hidden = false;
      document.body.classList.add('vpv-modal-open');
      modalEl.focus?.();
    }

    // ── Close modal ───────────────────────────────────────────────────────
    function closeModal() {
      if (!modalEl) return;

      // Stop playback by wiping content before hiding
      modalContent.innerHTML = '';
      modalEl.hidden = true;
      document.body.classList.remove('vpv-modal-open');
      playBtn.focus(); // return focus to trigger
    }

    // ── Play button click ─────────────────────────────────────────────────
    playBtn.addEventListener('click', () => {
      if (useModal) {
        playModal();
      } else {
        playInline();
      }
    });

    // ── Modal close triggers ──────────────────────────────────────────────
    if (modalEl) {
      modalEl.querySelectorAll('[data-vpv-modal-close]').forEach((el) => {
        el.addEventListener('click', closeModal);
      });

      // Esc key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !modalEl.hidden) closeModal();
      });
    }
  }

  // ── Init all blocks on the page ───────────────────────────────────────────

  function initAll() {
    document.querySelectorAll('[data-vpv-video-block]').forEach(initBlock);
  }

  // Run after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify theme editor section/block changes
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target?.closest('[data-vpv-video-block]');
    if (block) initBlock(block);
  });

  /*
   * ── Optional: Vimeo / YouTube API hooks for poster re-show ───────────────
   *
   * If you need the poster to re-appear when a Vimeo or YouTube video is
   * paused or ends, load the relevant SDK and add listeners here.
   *
   * Vimeo example (load https://player.vimeo.com/api/player.js first):
   *
   *   const vimeoPlayer = new Vimeo.Player(iframeEl);
   *   vimeoPlayer.on('pause', showPoster);
   *   vimeoPlayer.on('ended', showPoster);
   *
   * YouTube IFrame API example (load https://www.youtube.com/iframe_api first):
   *
   *   const ytPlayer = new YT.Player(iframeEl, {
   *     events: {
   *       onStateChange(event) {
   *         if ([YT.PlayerState.PAUSED, YT.PlayerState.ENDED].includes(event.data)) {
   *           showPoster();
   *         }
   *       }
   *     }
   *   });
   *
   * Both require restructuring playInline() slightly so that showPoster is
   * in scope when the SDK callback fires — hoist it out or pass a callback in.
   */

})();
